import { useState } from "react";
import type {
  DraftGenerationAttemptSummary,
  DraftGenerationSummary,
} from "../../api/queries/adminQueries";

type DraftPromptInspectorProps = {
  generation: DraftGenerationSummary;
};

const PromptBlock = ({
  label,
  text,
}: {
  label: string;
  text: string | null | undefined;
}) => {
  const [open, setOpen] = useState(false);
  if (!text?.trim()) {
    return null;
  }
  return (
    <div className="rounded border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50"
      >
        <span>{label}</span>
        <span className="text-slate-400">{open ? "−" : "+"}</span>
      </button>
      {open ? (
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap border-t border-slate-100 bg-slate-50 p-3 text-xs text-slate-800">
          {text}
        </pre>
      ) : null}
    </div>
  );
};

const JsonBlock = ({
  label,
  data,
}: {
  label: string;
  data: unknown;
}) => {
  if (data == null) {
    return null;
  }
  const text = JSON.stringify(data, null, 2);
  return <PromptBlock label={label} text={text} />;
};

const AttemptPromptPanel = ({
  attempt,
}: {
  attempt: DraftGenerationAttemptSummary;
}) => (
  <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/50 p-3">
    <p className="text-xs font-semibold text-slate-700">
      Attempt #{attempt.attempt_index} · {attempt.prompt_role} · {attempt.attempt_kind}
      {attempt.prompt_template_key ? ` · ${attempt.prompt_template_key}` : ""}
    </p>
    <PromptBlock label="System prompt" text={attempt.prompt_system} />
    <PromptBlock label="User prompt (values injected)" text={attempt.prompt_user} />
    <PromptBlock label="LLM response" text={attempt.raw_llm_response} />
    {attempt.guardrail_verdict ? (
      <p className="text-xs text-slate-600">
        <span className="font-medium">Guardrail:</span> {attempt.guardrail_verdict}
        {attempt.guardrail_detail ? ` — ${attempt.guardrail_detail}` : ""}
      </p>
    ) : null}
  </div>
);

export const DraftPromptInspector = ({ generation }: DraftPromptInspectorProps) => {
  const draftAttempts = generation.attempts.filter(
    (a) => a.prompt_role === "email_draft" || a.prompt_role === "email_correction",
  );
  const guardrailAttempts = generation.attempts.filter(
    (a) => a.prompt_role === "guardrail_validation",
  );

  return (
    <div className="mt-4 space-y-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Prompts & injected context
      </p>

      <JsonBlock label="Input snapshot (invoice, facts, style)" data={generation.input_snapshot} />

      {draftAttempts.length > 0 ? (
        <div className="space-y-3">
          <p className="text-xs font-medium text-slate-600">Draft generation</p>
          {draftAttempts.map((attempt) => (
            <AttemptPromptPanel key={attempt.id} attempt={attempt} />
          ))}
        </div>
      ) : null}

      {guardrailAttempts.length > 0 ? (
        <div className="space-y-3">
          <p className="text-xs font-medium text-slate-600">Guardrail validation</p>
          {guardrailAttempts.map((attempt) => (
            <AttemptPromptPanel key={attempt.id} attempt={attempt} />
          ))}
        </div>
      ) : null}
    </div>
  );
};
